// Generic client-side event beacon — the single standard for mirroring DOM
// interactions to server-side tracking. Any element carrying data-event-name is
// an event source; the script collects ALL of its data-* attributes, maps each
// to a POST param (data-object-id -> object_id, data-q -> q, …) and POSTs them
// to the store-event endpoint. No per-event logic lives here: the backend
// dispatches by `event`, picks the params it needs and logs a warning when a
// required attribute is missing — so instrumentation is driven purely from HTML.
//
// data-event-trigger picks how it fires: "click" (the default) on click, "view"
// once the element first scrolls into the viewport. The CSRF token comes from
// <body data-csrf-token> (it's per-request, so it stays in HTML).
//
// One pair of events is NOT declared in markup and is recognised here instead —
// a click on a link that leaves the shop, and a click on a link that hands over
// a file. See `resolveLinkExit` for why those two cannot be annotated.
//
// Every payload also carries the state every user event shares — the two
// clocks, the scroll depth, which page view this is. An event we were sending
// anyway is a free ride for that reading, and it has a real consequence: a page
// view where somebody clicked something straight away reports a live browser AT
// THE CLICK, rather than waiting for the first scheduled signal five seconds
// later. The state is attached in ONE place, `withUserEventState`, so no call
// site can forget.

import {getUserEventState, takeDisplayMeasurement} from "./core/user-event";

const STORE_EVENT_URL = "/xhr/common/set-state/";

// Extensions that make a link a FILE rather than a page. A published list
// rather than "anything after a dot": plenty of catalogue urls end in something
// that looks like an extension and is a slug, and a rule that guessed would
// file half the shop as downloads.
const FILE_EXTENSIONS = new Set([
  "pdf", "doc", "docx", "odt", "rtf", "txt",
  "xls", "xlsx", "xlsm", "ods", "csv",
  "ppt", "pptx", "odp",
  "zip", "rar", "7z", "gz", "tar",
  "mp3", "wav", "mp4", "avi", "mov", "mkv", "wmv",
  "dwg", "dxf", "eps", "ai", "psd",
  "apk", "exe", "dmg", "msi",
]);

// Schemes this pair is about. `mailto:` and `tel:` leave the page too, but they
// have event types of their own — filing them here would put a phone call in a
// report about traffic going to other websites.
const TRACKED_PROTOCOLS = new Set(["http:", "https:"]);

const LINK_TEXT_MAX_LENGTH = 200;

// Matches `tracking.xhr_views.BATCH_EVENT`.
const BATCH_EVENT = "batch";

// Events are queued and sent together rather than one request each. A listing
// scrolled from top to bottom brings every card's price box into view in turn,
// and each used to be a request of its own paying the whole server stack for
// one row: 2 079 an hour on production, the largest single source of beacon
// traffic. Nothing about WHAT is recorded changes — the server takes each event
// of a batch down the same path a single beacon takes — only how many requests
// carry it.
//
// Only IMPRESSIONS wait. Every other event is sent at once and takes the queue
// along, ahead of itself (`sendNow`), so the order on the wire is the order
// things happened: a click never reaches the server ahead of the impression it
// answers, and every click-through rate is read as impression → click.
//
// Three seconds after the first queued impression, or at twenty, whichever
// comes first. The queue also goes out on `pagehide` and when the tab is
// hidden, with `keepalive`, so a visitor who leaves inside the window loses
// nothing.
const EVENT_BATCH_DELAY_MS = 3000;
const EVENT_BATCH_LIMIT = 20;

let queuedEvents = [];
let eventFlushTimeout = null;

function toParam(datasetKey) {
  // camelCase dataset key -> snake_case POST param ("objectId" -> "object_id")
  return datasetKey.replace(/[A-Z]/g, (c) => "_" + c.toLowerCase());
}

// The shared state goes in FIRST, so anything the caller states explicitly
// lands after it and wins — Django's POST.get keeps the last of a repeated key.
function withUserEventState() {
  const body = new FormData();
  const state = getUserEventState();
  Object.keys(state).forEach((key) => body.append(key, state[key]));
  // The screen and the window, if this is the first beacon of the page or the
  // window has moved since the last one. Taken here as well as in the pulse so
  // a page whose visitor clicks something in the first five seconds reports its
  // layout at the click rather than at the first scheduled signal.
  const display = takeDisplayMeasurement();
  Object.keys(display).forEach((key) => body.append(key, display[key]));
  return body;
}

// What the element says about itself, as `[param, value]` pairs — every data-*
// on it, plus the gaps a late-arriving fragment fills. The page state is NOT
// here: `flushEvents` puts it in front of the request, once for all of its
// events.
function elementParams(el) {
  // Send every data-* on the element; the backend keeps what it recognises.
  const params = [];
  const sent = new Set();
  Object.keys(el.dataset).forEach((key) => {
    const value = el.dataset[key];
    if (value === "") return;
    sent.add(key);
    params.push([key === "eventName" ? "event" : toParam(key), value]);
  });

  // What a late-arriving fragment knows about the block it landed in. A
  // deferred block's container is rendered before its products exist, so it
  // cannot state how many it holds, and htmx swaps the CHILDREN — the
  // container's own attributes are never revisited. The fragment marks itself
  // `data-beacon-extra` and the values it carries fill the gaps.
  //
  // Fills gaps only: anything the container stated itself wins, because the
  // container is what the measurement is about and a fragment must not be able
  // to rename the block it was swapped into.
  const extra = el.querySelector("[data-beacon-extra]");
  if (extra) {
    Object.keys(extra.dataset).forEach((key) => {
      const value = extra.dataset[key];
      if (value === "" || key === "beaconExtra" || sent.has(key)) return;
      params.push([toParam(key), value]);
    });
  }
  return params;
}

// Send whatever events are waiting, in one request. A lone event goes as the
// plain beacon it always was; only two or more make a batch. The page state is
// read HERE, at send time, so the request reports the page as it is when it
// leaves — the same reading a pulse at that moment would carry.
function flushEvents() {
  if (eventFlushTimeout !== null) {
    clearTimeout(eventFlushTimeout);
    eventFlushTimeout = null;
  }
  if (!queuedEvents.length) return;
  const queued = queuedEvents;
  queuedEvents = [];

  const body = withUserEventState();
  if (queued.length === 1) {
    queued[0].forEach(([key, value]) => body.append(key, value));
  } else {
    body.append("event", BATCH_EVENT);
    body.append("events", JSON.stringify(queued.map((params) => Object.fromEntries(params))));
  }
  fetch(STORE_EVENT_URL, {
    method: "POST",
    headers: { "X-CSRFToken": (document.body && document.body.dataset.csrfToken) || "" },
    body,
    keepalive: true, // outlive the navigation a click starts
  }).catch(() => {});
}

// Queue one event, as `[param, value]` pairs read NOW rather than at flush
// time: by then htmx may have swapped the element out of the document, and
// what it said when it fired is the measurement.
function queueEvent(params) {
  queuedEvents.push(params);
  if (queuedEvents.length >= EVENT_BATCH_LIMIT) {
    flushEvents();
  } else if (eventFlushTimeout === null) {
    eventFlushTimeout = setTimeout(flushEvents, EVENT_BATCH_DELAY_MS);
  }
}

// Send one event NOW, taking whatever is queued along in the same request,
// ahead of it. Everything that is not an impression goes this way: a click, a
// read code, a result picked in the search box, the end of a menu walk — most
// of them are the last thing before a navigation, and several are sent FROM a
// `pagehide` handler of their own, which may run after ours has already
// emptied the queue. Waiting would lose them; sending them alone would put
// them ahead of the impressions they answer.
function sendNow(params) {
  queuedEvents.push(params);
  flushEvents();
}

function beacon(el) {
  if (!el.dataset.eventName) return;
  sendNow(elementParams(el));
}

// An impression waits in the queue; see `EVENT_BATCH_DELAY_MS`.
function queueImpression(el) {
  if (!el.dataset.eventName) return;
  queueEvent(elementParams(el));
}

// Programmatic counterpart of the data-* beacon, for events no DOM interaction
// produces — media playback reaching a milestone, say. `params` are sent as-is,
// so callers use the snake_case names the backend expects. `el` is accepted for
// the callers that pass it; the endpoint is CSRF-exempt (see `store_event`), so
// which token rides along no longer decides anything.
export function sendEvent(name, params, el) {
  const pairs = [["event", name]];
  Object.keys(params || {}).forEach((key) => {
    const value = params[key];
    if (value === undefined || value === null || value === "") return;
    pairs.push([key, value]);
  });
  sendNow(pairs);
}

// What a clicked link means, or null when it means nothing worth a row.
//
// Outbound is decided by the HOST — anything that is not this one takes the
// visitor somewhere we can no longer see. A download is decided by the
// RESOURCE: an explicit `download` attribute, or a path ending in a published
// file extension. The download question is asked first and without looking at
// the host, because a price list on a partner's server is still a document
// somebody asked for, and asking about the host first would file the same pdf
// two different ways depending on where it happened to be hosted.
function resolveLinkExit(link) {
  const href = link.getAttribute("href");
  if (!href) return null;

  let url;
  try {
    // Resolved against the page rather than read off `link.href`, which an
    // `<a>` inside an SVG does not provide as a string.
    url = new URL(href, window.location.href);
  } catch (error) {
    return null;
  }
  if (!TRACKED_PROTOCOLS.has(url.protocol)) return null;

  const name = url.pathname.split("/").filter(Boolean).pop() || "";
  const dot = name.lastIndexOf(".");
  const extension = dot > 0 ? name.slice(dot + 1).toLowerCase() : "";

  if (link.hasAttribute("download") || FILE_EXTENSIONS.has(extension)) {
    return { event: "file_download", url, extension };
  }
  if (url.host !== window.location.host) {
    return { event: "outbound_click", url, extension: "" };
  }
  return null;
}

export function init() {
  queuedEvents = [];
  if (eventFlushTimeout !== null) {
    clearTimeout(eventFlushTimeout);
    eventFlushTimeout = null;
  }
  // A page going away, or a tab put in the background that may never be looked
  // at again, sends what is queued now — with `keepalive`, so the request
  // outlives the page. The same two moments the pulse says goodbye on.
  window.addEventListener("pagehide", flushEvents);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") flushEvents();
  });

  // Links that take the visitor away, and links that hand them a file. NOT
  // driven by `data-event-name`, unlike everything else in this module, and the
  // exception is deliberate: these links are written by editors in CMS content
  // and by a hundred templates, so an instrumentation that had to be remembered
  // would measure whichever half somebody thought of. The declarative channel
  // still wins where it is present — an element that says what its click means
  // is not also guessed at (`TRACK-EVENT-024`).
  //
  // The beacon survives the navigation the click starts because `post` sends it
  // with `keepalive`; without that the browser would cancel the request as it
  // tore the page down, and the event most worth having — the one describing
  // somebody leaving — would be the one that never arrived.
  document.addEventListener("click", (event) => {
    const link = event.target.closest("a[href]");
    if (!link || link.closest("[data-event-name]")) return;

    const exit = resolveLinkExit(link);
    if (!exit) return;

    sendEvent(exit.event, {
      link_url: exit.url.href,
      link_text: (link.textContent || "").trim().slice(0, LINK_TEXT_MAX_LENGTH),
      file_extension: exit.extension,
    }, link);
  });

  // Delegate on `document` (not document.body) — app.js runs in <head> before
  // <body> exists, and events bubble up to document anyway. So htmx-swapped
  // content is covered too.
  document.addEventListener("click", (event) => {
    const el = event.target.closest('[data-event-name]:not([data-event-trigger="view"])');
    if (el) beacon(el);
  });

  // One node, two events. An element is a view beacon OR a click beacon — the
  // selector above says so — and that is right almost everywhere, because the
  // block that comes into view and the link inside it are different nodes.
  //
  // The exception is a block that IS its own control: the wholesale price box
  // is one box, seen and clicked, and it reported only the impression. The
  // usual answer is to wrap it in a second element carrying the other half,
  // which here would put a new node between a flex parent and the box it lays
  // out — a measurement change with a visual risk attached, on the element that
  // appears on every card in the shop.
  //
  // So the element declares the click instead. `data-click-event` names it and
  // the attributes already on the node describe it, which also means the two
  // events cannot drift apart: they are read from one place.
  document.addEventListener("click", (event) => {
    const el = event.target.closest('[data-click-event][data-event-trigger="view"]');
    if (!el) return;
    sendNow(elementParams(el)
      .filter(([key]) => key !== "click_event" && key !== "event_trigger")
      .map(([key, value]) => (key === "event" ? [key, el.dataset.clickEvent] : [key, value])));
  });

  // How much of a block has to be on screen before it counts as seen, in CSS
  // pixels of its own height. A RATIO cannot express this: `threshold` measures
  // the share of the TARGET that is visible, and a block of six stacked cards
  // is around 3000px tall against a 700px phone viewport — its maximum
  // achievable ratio is roughly 0.23, so any threshold above that would never
  // fire on a phone and the impressions would quietly become desktop-only.
  //
  // 200px is about one whole product photo, which is the rule stated in plain
  // words: the visitor saw a tile, not the edge of one.
  const MIN_VISIBLE_PX = 200;

  // How long that has to hold. Without it a fast scroll banks every block on
  // the page, and the impression count — the denominator of every rate the
  // grids feed — measures scrolling rather than looking. One second is the IAB
  // display standard and is what the numbers below are comparable to.
  const DWELL_MS = 1000;

  // Timer per element, so leaving the viewport cancels rather than defers.
  const pending = new WeakMap();

  const cancel = (el) => {
    const timer = pending.get(el);
    if (timer) {
      clearTimeout(timer);
      pending.delete(el);
    }
  };

  // Fire once, when enough of the element has been on screen for long enough.
  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      const seenEnough = entry.isIntersecting
        && entry.intersectionRect.height >= Math.min(
          MIN_VISIBLE_PX, entry.boundingClientRect.height);

      if (!seenEnough) {
        cancel(entry.target);
        return;
      }
      if (pending.has(entry.target)) return;

      pending.set(entry.target, setTimeout(() => {
        pending.delete(entry.target);
        queueImpression(entry.target);
        observer.unobserve(entry.target);
      }, DWELL_MS));
    });
    // `threshold` still carries a ladder of ratios: the callback only runs when
    // one is crossed, so a block scrolled slowly past would otherwise never be
    // re-measured after its first, too-small intersection.
  }, { threshold: [0, 0.05, 0.1, 0.2, 0.3, 0.5, 0.75, 1] });

  const VIEW = '[data-event-name][data-event-trigger="view"]';
  const observe = (root) => {
    if (root.matches && root.matches(VIEW)) observer.observe(root);
    if (root.querySelectorAll) {
      root.querySelectorAll(VIEW).forEach((el) => observer.observe(el));
    }
  };
  // The first sweep waits for the document to exist. `app.js` is a plain
  // `<script>` in `<head>`, so `init()` runs before `<body>` is parsed and a
  // `querySelectorAll` here matches NOTHING — every beacon written into the
  // page was silently never observed, while blocks htmx swapped in later were
  // picked up by the listener below and worked. That is why the deferred
  // product blocks reported impressions and the eager ones, the banners and
  // the FloralPass strips never did: not a network fault and not a threshold,
  // one sweep against an empty document.
  //
  // The click delegation above does not have this problem — it listens on
  // `document`, which exists, and events bubble to it whenever the elements
  // appear. Only the observer has to be handed real elements.
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => observe(document), {once: true});
  } else {
    observe(document);
  }
  document.addEventListener("htmx:afterSwap", (event) => observe(event.target));
}
